// src/features/staff/pages/ProfilePage/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';

// Components
import ProfileHeader from './components/ProfileHeader';
import UserCard from './components/UserCard';
import AccountInfoCard from './components/AccountInfoCard';
import RolesCard from './components/RolesCard';
import ChangePasswordDialog from './components/ChangePasswordDialog';
import SetPinDialog from '../../../auth/pages/ChangeUserPage/componenets/SetPinDialog';

// Hooks
import { useAuth } from '../../../../context/AuthContext';
import { useStaff } from '../../hooks/useStaff';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import PageHeader from '../../../../components/common/PageHeader';
import PersonIcon from '@mui/icons-material/Person';

export default function ProfilePage() {
  const { t } = useTranslation(['staff']);
  const { user: authUser } = useAuth();
  const { showError, showSuccess } = useSnackbar();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [pinDialogMode, setPinDialogMode] = useState(null); // null | 'profile' | 'disable'
  const [pinSaving, setPinSaving] = useState(false);
  const [pinDialogError, setPinDialogError] = useState(null);

  const {
    fetchCurrentStaff,
    changeCurrentStaffPassword,
    setCurrentStaffPin,
    disableCurrentStaffPin,
    error,
    clearError,
  } = useStaff();

  // Load profile data on mount
  useEffect(() => {
    const loadProfileData = async () => {
      try {
        setLoading(true);
        const result = await fetchCurrentStaff();
        if (result.success) {
          setProfileData(result.data);
        } else {
          showError(t('staff:errors.load_profile_failed'));
        }
      } catch (error) {
        showError(t('staff:errors.load_profile_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();
  }, [fetchCurrentStaff, showError, t]);

  const handlePasswordChange = async (currentPassword, newPassword) => {
    setChangingPassword(true);
    try {
      const result = await changeCurrentStaffPassword(currentPassword, newPassword);
      
      if (result.success) {
        showSuccess(t('staff:profile.password_changed_success'));
        
        // Handle session refresh if needed
        if (result.data?.requires_session_refresh) {
          window.location.reload();
        }
      }
      
      return result;
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePinConfirm = async ({ pin, currentPassword }) => {
    setPinSaving(true);
    setPinDialogError(null);
    try {
      const result = pinDialogMode === 'disable'
        ? await disableCurrentStaffPin(currentPassword)
        : await setCurrentStaffPin(currentPassword, pin);

      if (result.success) {
        showSuccess(t(pinDialogMode === 'disable' ? 'staff:profile.pin_disabled_success' : 'staff:profile.pin_set_success'));
        setPinDialogMode(null);
        setProfileData((prev) => prev && { ...prev, pin_enabled: pinDialogMode !== 'disable' });
      } else {
        setPinDialogError(result.error || t(pinDialogMode === 'disable' ? 'staff:profile.pin_disable_failed' : 'staff:profile.pin_set_failed'));
      }
    } finally {
      setPinSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!profileData) {
    return (
      <Box>
        <PageHeader icon={PersonIcon} title={t('staff:profile.title')} />
        <Alert severity="error">
          {t('staff:profile.error_loading')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <ProfileHeader
        onChangePassword={() => setPasswordDialogOpen(true)}
        onManagePin={() => { setPinDialogError(null); setPinDialogMode('profile'); }}
        onDisablePin={() => { setPinDialogError(null); setPinDialogMode('disable'); }}
        pinEnabled={!!profileData.pin_enabled}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Box display="flex" gap={3} alignItems={"stretch"} flexWrap="wrap">
        {/* User Overview */}
        <Box flex={1} minWidth={210}>
          <UserCard profileData={profileData} />
        </Box>

        {/* Details Cards */}
        <Box flex={2}>
          <Stack spacing={3}>
            <AccountInfoCard profileData={profileData} />
            <RolesCard profileData={profileData} />
          </Stack>
        </Box>
      </Box>

      {/* Password Change Dialog */}
      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        onPasswordChange={handlePasswordChange}
        loading={changingPassword}
      />

      {/* PIN Set/Change/Remove Dialog */}
      <SetPinDialog
        open={!!pinDialogMode}
        mode={pinDialogMode || 'profile'}
        onConfirm={handlePinConfirm}
        onClose={() => { setPinDialogMode(null); setPinDialogError(null); }}
        loading={pinSaving}
        serverError={pinDialogError}
        onClearError={() => setPinDialogError(null)}
      />
    </Box>
  );
}